/**
 * Classifies section cards as `is-expired` / `is-current` / `is-upcoming`
 * relative to "now" in the agenda's timezone. Same class names on both views
 * (calendar + list); per-view styling differs only in which accent token the
 * `is-current` outline picks up.
 *
 * Mount once per agenda container — a 30 s wall-clock tick re-classifies as
 * sections cross their start/end boundaries. The bootstraps additionally call
 * `applySectionStates()` from inside their poll `onUpdate` rAF block so a
 * delay broadcast that flips a section in or out of "now" is reflected
 * within the same frame as the time-text and position update.
 *
 * Gated on today-in-tz, matching `mountNowLine`. Past or future agendas get
 * no classes; if a session crosses midnight, the per-tick gate clears the
 * classes automatically.
 */
import { TICK_MS, clockOffset, minutesNowInTz, todayInTz, } from "./now-line";
export const STATE_CLASSES = {
    expired: "is-expired",
    current: "is-current",
    upcoming: "is-upcoming",
};
const CALENDAR_CARD_SELECTOR = ".hora-calendar__section[data-section-id]";
const LIST_CARD_SELECTOR = ".hora-agenda-list__section[data-section-id]";
const ALL_STATE_CLASSES = [
    STATE_CLASSES.expired,
    STATE_CLASSES.current,
    STATE_CLASSES.upcoming,
];
function cardSelector(view) {
    return view === "calendar" ? CALENDAR_CARD_SELECTOR : LIST_CARD_SELECTOR;
}
function readMinAttr(el, primary, fallback) {
    const eff = el.getAttribute(primary);
    if (eff !== null && eff !== "") {
        const n = Number(eff);
        if (Number.isFinite(n))
            return n;
    }
    const base = el.getAttribute(fallback);
    if (base !== null && base !== "") {
        const n = Number(base);
        if (Number.isFinite(n))
            return n;
    }
    return null;
}
function classifyCard(card, nowMin) {
    const start = readMinAttr(card, "data-effective-start-min", "data-start-min");
    if (start === null)
        return null;
    const end = readMinAttr(card, "data-effective-end-min", "data-end-min");
    if (end !== null && nowMin >= end)
        return "expired";
    if (nowMin >= start)
        return "current";
    return "upcoming";
}
function applyClass(card, state) {
    const target = state ? STATE_CLASSES[state] : null;
    for (const cls of ALL_STATE_CLASSES) {
        card.classList.toggle(cls, cls === target);
    }
}
function clearClasses(card) {
    for (const cls of ALL_STATE_CLASSES) {
        card.classList.remove(cls);
    }
}
export function applySectionStates(opts) {
    var _a;
    const cards = opts.container.querySelectorAll(cardSelector(opts.view));
    if (cards.length === 0)
        return;
    if (!opts.agendaDate || !opts.tz || opts.agendaDate !== todayInTz(opts.tz)) {
        cards.forEach(clearClasses);
        return;
    }
    const offsetMs = clockOffset((_a = opts.serverNow) !== null && _a !== void 0 ? _a : null);
    const nowMin = minutesNowInTz(opts.tz, offsetMs);
    cards.forEach((card) => applyClass(card, classifyCard(card, nowMin)));
}
export function mountSectionStates(opts) {
    applySectionStates(opts);
    const timer = window.setInterval(() => applySectionStates(opts), TICK_MS);
    return () => {
        window.clearInterval(timer);
    };
}
